import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faKey, faCheck, faXmark, faUser } from '@fortawesome/free-solid-svg-icons';

const PermissionDialog = ({ 
  isOpen, 
  onClose, 
  user, 
  permissions, 
  selectedPermissions, 
  onPermissionChange, 
  onSave, 
  isLoading = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
      ></div>
      
      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white bg-opacity-20">
                  <FontAwesomeIcon icon={faKey} className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">إدارة الأذونات</h3>
                  <p className="text-sm text-blue-100">
                    تعديل الأذونات لـ <span className="font-semibold">{user?.userName || ''}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* User Info Card */}
            <div className="mb-6 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-5">
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon icon={faUser} className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900">{user?.userName || ''}</h4>
                  <p className="text-sm text-gray-600 mb-1">{user?.displayName || ''}</p>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                    {user?.userType || 'مستخدم'}
                  </span>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FontAwesomeIcon icon={faKey} className="h-5 w-5 ml-2 text-blue-600" />
                  الأذونات المتاحة
                </h4>
                <div className="text-sm text-gray-500">
                  {selectedPermissions.length} من {permissions?.data?.length || 0} محدد
                </div>
              </div>
              
              {/* Permissions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto pr-2">
                {permissions?.data?.map((permission) => {
                  const isSelected = selectedPermissions.includes(permission.name);
                  return (
                    <div
                      key={permission.id}
                      className={`relative rounded-xl border-2 p-4 transition-all duration-200 cursor-pointer group ${
                        isSelected
                          ? 'border-green-500 bg-green-50 shadow-md transform scale-105'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 hover:shadow-md'
                      }`}
                      onClick={() => onPermissionChange(permission.name)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                          <div className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
                            isSelected ? 'bg-green-500' : 'bg-gray-200 group-hover:bg-blue-200'
                          }`}>
                            {isSelected ? (
                              <FontAwesomeIcon icon={faCheck} className="h-5 w-5 text-white" />
                            ) : (
                              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <span className={`font-semibold text-sm ${
                              isSelected ? 'text-green-800' : 'text-gray-700'
                            }`}>
                              {permission.name}
                            </span>
                          </div>
                        </div>
                        
                        {/* Custom Toggle Switch */}
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          isSelected ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-md ${
                            isSelected ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Card */}
            <div className="mt-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-5 border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-900">الأذونات المحددة</p>
                  <p className="text-3xl font-bold text-blue-600">{selectedPermissions.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-900">إجمالي الأذونات</p>
                  <p className="text-3xl font-bold text-blue-600">{permissions?.data?.length || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-900">النسبة المئوية</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {permissions?.data?.length > 0 
                      ? Math.round((selectedPermissions.length / permissions.data.length) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={onSave}
                disabled={isLoading}
                className="px-8 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rtl:space-x-reverse"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>جاري الحفظ...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
                    <span>حفظ الأذونات</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionDialog;
